import { useState } from "react";
import { X, Layers, Loader2, Eye } from "lucide-react";
import { languageSkillsAPI } from "../../services/languageService";

const getApiError = (err) => {
  const d = err?.response?.data;
  if (!d) return "حدث خطأ غير متوقع";
  if (typeof d === "string") return d;
  if (d.error) return d.error;
  const first = Object.values(d)[0];
  return Array.isArray(first) ? first[0] : String(first);
};

export default function MergeMixedModal({
  categoryId,
  candidates,
  target,
  onClose,
  onDone,
}) {
  const [selected, setSelected] = useState(() => new Set(candidates.map((s) => s.id)));
  const [title, setTitle] = useState("كل المسارات الميكسيد");
  const [deactivate, setDeactivate] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggle = (id) => {
    setPreview(null);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const run = async (dryRun) => {
    if (selected.size === 0) {
      setError("اختار مهارة واحدة على الأقل");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const payload = {
        source_skill_ids: [...selected],
        deactivate_sources: deactivate,
        dry_run: dryRun,
        ...(target ? { target_skill_id: target.id } : { title }),
      };
      const data = await languageSkillsAPI.mergeMixed(categoryId, payload);
      if (dryRun) setPreview(data);
      else onDone(data);
    } catch (err) {
      console.error(err);
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-gray-900">
              {target ? `تحديث: ${target.title}` : "دمج المهارات الميكسيد"}
            </h2>
            <Layers className="w-5 h-5 text-purple-600" />
          </div>
        </div>

        {!target && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
              عنوان المهارة المجمّعة
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-right focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2 text-right">
            المهارات المصدر ({selected.size}/{candidates.length})
          </p>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {candidates.map((s) => (
              <label
                key={s.id}
                className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <span className="text-xs text-gray-400">{s.total_questions || 0} Q</span>
                <span className="flex items-center gap-2 text-sm text-gray-800">
                  {s.title}
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="accent-purple-600"
                  />
                </span>
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center justify-end gap-2 text-sm text-gray-700 cursor-pointer">
          تعطيل المهارات الأصلية بعد النسخ
          <input
            type="checkbox"
            checked={deactivate}
            onChange={(e) => setDeactivate(e.target.checked)}
            className="accent-purple-600"
          />
        </label>

        {preview && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-sm text-right space-y-1">
            <p className="font-semibold text-purple-800">
              معاينة: هيتنسخ {preview.total_copied} عنصر
            </p>
            {preview.sources.map((r) => (
              <p key={r.skill_id} className="text-purple-700">
                {r.title}: نسخ {r.copied} · تخطي {r.skipped}
              </p>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm text-right">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button
            onClick={() => run(false)}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-medium"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {target ? "تحديث" : "دمج"}
          </button>
          <button
            onClick={() => run(true)}
            disabled={loading}
            className="flex items-center justify-center gap-2 border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-60 px-4 py-2.5 rounded-xl font-medium"
          >
            <Eye className="w-4 h-4" />
            معاينة
          </button>
        </div>
      </div>
    </div>
  );
}